import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment'
import { of, Subject } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import { ServicePlanReconciliationItemDto } from 'src/app/models/service-plan/service-plan-reconciliation-item-dto'
import { ServicePlanReconciliationTableColumnDefinitions } from 'src/app/models/table-defs/service-plan-reconciliation-table-def'
import { TableColumnDefinition } from 'src/app/models/table/table-column-definition'
import { TableLinkColumnDTO } from 'src/app/models/table/table-link-column-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ServicePlanReconciliationService } from 'src/app/services/service-plan/service-plan-reconciliation.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'

// Table row shape — a dashboard deep-link for the client name (same TableLinkColumnDTO mechanism
// ClientTableDTO.preferredFullName already uses) plus one formatted display string per step
// timestamp (check/pending icon-and-text, never colour alone — pre-review-checklist.md).
class ServicePlanReconciliationTableDTO {
  clientName: TableLinkColumnDTO
  dateOfBirth: string
  submittedOn: string
  pdfGeneratedOn: string
  emailSentOn: string
  pdfAttachedOn: string
  noteCreatedOn: string
  failureReason: string
}

const DEFAULT_WINDOW_DAYS = 30
const DATE_FORMAT = 'Do MMMM, YYYY'
const DATE_TIME_FORMAT = 'Do MMMM, YYYY h:mm a'

@Component({
  selector: 'app-reconciliation-view',
  templateUrl: './reconciliation-view.component.html',
  styleUrls: ['./reconciliation-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReconciliationViewComponent implements OnInit {
  _tableColumnsToDisplay: string[] = [
    'clientName',
    'dateOfBirth',
    'submittedOn',
    'pdfGeneratedOn',
    'emailSentOn',
    'pdfAttachedOn',
    'noteCreatedOn',
    'failureReason',
  ]
  _tableColumnDefinitions: TableColumnDefinition[] =
    ServicePlanReconciliationTableColumnDefinitions
  _tableData: MatTableDataSource<ServicePlanReconciliationTableDTO> =
    new MatTableDataSource<ServicePlanReconciliationTableDTO>()
  _incompleteOnly = false
  _dataLoaded = false

  // A Subject + switchMap (Review Finding, e10-1 code review) rather than a plain re-invoked
  // subscribe — switchMap cancels any still-in-flight GetReconciliation call when the toggle
  // changes again, so a stale response can't overwrite a later one or mis-fire the spinner.
  private incompleteOnlyChange$ = new Subject<boolean>()

  constructor(
    private reconciliationService: ServicePlanReconciliationService,
    private spinnerService: SpinnerService,
    private bannerService: BannerService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.incompleteOnlyChange$
      .pipe(
        switchMap((incompleteOnly) => {
          this.spinnerService.show()
          return this.reconciliationService
            .GetReconciliation(DEFAULT_WINDOW_DAYS, incompleteOnly)
            .pipe(
              map((items) => ({ items })),
              // Caught per-request, inside the inner pipe (Review Finding, e10-1 code review) —
              // an error here must not propagate and kill the outer switchMap subscription,
              // which would otherwise silently stop the toggle from ever re-querying again.
              catchError(() => of({ error: true as const })),
            )
        }),
      )
      .subscribe((result) => {
        if ('error' in result) {
          this.spinnerService.hide()
          this.bannerService.showBanner(
            this.translate.instant('SERVICE_PLAN_RECONCILIATION.LOAD_ERROR'),
            'error',
            30000,
          )
          this.cd.markForCheck()
          return
        }

        this._tableData = this.mapReconciliationData(result.items)
        this._dataLoaded = true
        this.spinnerService.hide()
        this.cd.markForCheck()
      })

    this.incompleteOnlyChange$.next(this._incompleteOnly)
  }

  onIncompleteOnlyChange(incompleteOnly: boolean) {
    this._incompleteOnly = incompleteOnly
    this.incompleteOnlyChange$.next(incompleteOnly)
  }

  private mapReconciliationData(
    items: ServicePlanReconciliationItemDto[],
  ): MatTableDataSource<ServicePlanReconciliationTableDTO> {
    const tableData = items.map((item) => {
      const target = new ServicePlanReconciliationTableDTO()

      target.clientName = new TableLinkColumnDTO()
      target.clientName.linkText =
        // Falsy check (Review Finding, e10-1 code review) — ?? only catches null/undefined, not
        // an empty string, which would otherwise render a blank, unlabeled link.
        item.clientPreferredFullName ||
        this.translate.instant('SERVICE_PLAN_RECONCILIATION.UNKNOWN_CLIENT')
      target.clientName.linkUrl = '/client-dashboard/' + item.clientId
      target.clientName.linkData = item.clientId
      target.clientName.selected = false

      // UTC (Review Finding, e10-1 code review) — DOB is a date-only value serialized as a UTC
      // DateTime; parsing it in the browser's local zone can shift the displayed calendar day.
      target.dateOfBirth = item.clientDateOfBirth
        ? moment.utc(item.clientDateOfBirth).format(DATE_FORMAT)
        : ''
      target.submittedOn = moment(item.submittedOn).format(DATE_TIME_FORMAT)
      target.pdfGeneratedOn = this.formatStepStatus(item.pdfGeneratedOn)
      target.emailSentOn = this.formatStepStatus(item.emailSentOn)
      target.pdfAttachedOn = this.formatStepStatus(item.pdfAttachedOn)
      target.noteCreatedOn = this.formatStepStatus(item.noteCreatedOn)
      target.failureReason = item.failureReason ?? ''

      return target
    })

    return new MatTableDataSource<ServicePlanReconciliationTableDTO>(tableData)
  }

  private formatStepStatus(timestamp: string | null): string {
    // Icon lives in the translation value itself (Review Finding, e10-1 code review), not
    // concatenated in code, so translators own the full label rather than an inconsistently
    // decorated plain string.
    return timestamp
      ? this.translate.instant('SERVICE_PLAN_RECONCILIATION.STEP_COMPLETE')
      : this.translate.instant('SERVICE_PLAN_RECONCILIATION.STEP_PENDING')
  }
}
